import {Injectable} from "@angular/core";
import {Store} from "@ngrx/store";
import {filter, map, Observable, switchMap, take, withLatestFrom} from "rxjs";
import {selectBackendApi} from "../store/config/config.selector";
import {HttpClient} from "@angular/common/http";
import {MapResultI, MapResultResponseI, ProcessResultI} from "../interfaces/process-result.interface";
import {ActivityI, TaskI, TaskPermissionsObjectI} from "../interfaces/task.interface";
import {TaskStateI} from "../interfaces/process.interface";
import {combineLatest} from "rxjs/operators";
import {selectSearchParameters} from "../store/process-result/process-result.selector";

@Injectable({
  providedIn: "root",
})
export class ProcessResultService {

  constructor(
    private store: Store,
    private http: HttpClient
  ) {}

  getProcessResultMap(
    processId: string,
    page: number | string,
    limit: number | string,
    bookmarkedFilter: string | null | boolean,
    processStatusFilter: string[] | string | null,
    processTaskFilter: string[] | string | null,
    dynamicFilter: any,
    globalSearch: string
  ): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        let body: any = {}
        if (page) body.page=`${page}`
        if (limit) body.limit=`${limit}`
        if (bookmarkedFilter) body.bookmarked=`${bookmarkedFilter}`
        if (processStatusFilter) body.process_status=`${processStatusFilter}`
        if (processTaskFilter) body.process_tasks=`${processTaskFilter}`
        if (globalSearch) body.search=`${globalSearch}`
        if (dynamicFilter) {
          const dynamicFilterStr = Object.keys(dynamicFilter).reduce((acc: string, curr: string) => {
            if (dynamicFilter[curr]) return acc + `$${curr}=${dynamicFilter[curr]}`;
            return acc;
          }, "").slice(1)
          if (dynamicFilterStr) body.filter = dynamicFilterStr;
        }

        return this.http.get(`${backendApiUrl}/process/${processId}/map-list`, body).pipe(
          map((data: any) => {
            return {
              ...data,
              results: data.results.map((result: any[]) => ({
                id: result[0],
                percentComplete: result[1],
                lat: result[2],
                lng: result[3],
                color: result[4]
              }))
            }
          })
        )
      })
    )
  }

  getProcessResultKanban(
    processId: string,
    page: number | string,
    limit: number | string,
    processStatusFilter: string[] | string | null,
  ): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      withLatestFrom(this.store.select(selectSearchParameters)),
      switchMap(([backendApiUrl, searchParameters]) => {
        let body: any = {}

        if (searchParameters.page)  body.page = `${searchParameters.page}`;
        if (searchParameters.entries) body.limit = `${searchParameters.entries}`;
        if (searchParameters.bookmarked_filter === 'active') body.bookmarked_filter = searchParameters.bookmarked_filter;
        if (searchParameters.process_status_filter) body.process_status = searchParameters.process_status_filter;
        if (searchParameters.process_tasks_filter) body.process_tasks = searchParameters.process_tasks_filter;
        if (searchParameters.search) body.search = searchParameters.search;
        if (searchParameters.orderBy) body.order_by = searchParameters.orderBy;
        if (searchParameters.order) body.order = searchParameters.order;
        if (searchParameters.dynamic_filter) {
          const dynamicFilterStr = Object.keys(searchParameters.dynamic_filter).reduce((acc: string, curr: string) => {
            if (searchParameters.dynamic_filter[curr]) return acc + `$${curr}=${searchParameters.dynamic_filter[curr]}`;
            return acc;
          }, "").slice(1)
          if (dynamicFilterStr) body.filter = dynamicFilterStr;
        }

        return this.http.post(`${backendApiUrl}/process/${processId}/kanban-list`, body)
      })
    )
  }

  getProcessResultList(
    processId: string,
    page: number | string,
    limit: number | string,
    bookmarkedFilter: string | null | boolean,
    processStatusFilter: string[] | string | null,
    processTaskFilter: string[] | string | null,
    dynamicFilter: any,
    globalSearch: string
  ): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        const params = [];
        if (page) params.push(`page=${page}`)
        if (limit) params.push(`limit=${limit}`)
        if (bookmarkedFilter) params.push(`bookmarked=${bookmarkedFilter}`)
        if (processStatusFilter) params.push(`process_status=${processStatusFilter}`)
        if (processTaskFilter) params.push(`process_tasks=${processTaskFilter}`)
        if (globalSearch) params.push(`search=${globalSearch}`)
        if (dynamicFilter) {
          const dynamicFilterStr = Object.keys(dynamicFilter).reduce((acc: string, curr: string) => {
            if (dynamicFilter[curr]) return acc + `$${curr}=${dynamicFilter[curr]}`;
            return acc;
          }, "").slice(1)
          if (dynamicFilterStr) params.push(`filter=${dynamicFilterStr}`)
        }
        const queryParams = params.length > 0 ? '?' + params.join('&') : ''

        return this.http.get(`${backendApiUrl}/process/${processId}/result-list${queryParams}`)
      })
    )
  }

  getProcessResults(
    processId: string,
  ): Observable<any> {
    return this.store.select(selectBackendApi)
      .pipe(
        filter((backendApiUrl) => backendApiUrl !== ""),
        withLatestFrom(this.store.select(selectSearchParameters)),
        switchMap(([backendApiUrl, searchParameters]) => {

          let body: any = {}

          if (searchParameters.page)  body.page = `${searchParameters.page}`;
          if (searchParameters.entries) body.limit = `${searchParameters.entries}`;
          if (searchParameters.bookmarked_filter === 'active') body.bookmarked_filter = searchParameters.bookmarked_filter;
          if (searchParameters.process_status_filter) body.process_status = searchParameters.process_status_filter;
          if (searchParameters.process_tasks_filter) body.process_tasks = searchParameters.process_tasks_filter;
          if (searchParameters.search) body.search = searchParameters.search;
          if (searchParameters.orderBy) body.order_by = searchParameters.orderBy;
          if (searchParameters.order) body.order = searchParameters.order;
          if (searchParameters.dynamic_filter) {
            const dynamicFilterStr = Object.keys(searchParameters.dynamic_filter).reduce((acc: string, curr: string) => {
              if (searchParameters.dynamic_filter[curr]) return acc + `$${curr}=${searchParameters.dynamic_filter[curr]}`;
              return acc;
            }, "").slice(1)
            if (dynamicFilterStr) body.filter = dynamicFilterStr;
          }
          return this.http.post(`${backendApiUrl}/process/${processId}/result-list-search`, body)
        })
      )
  }


  getBulkProcessResults(data:{
    processId: string,
    objectIds: string[],
  }): Observable<any> {
    return this.store.select(selectBackendApi)
      .pipe(
        filter((backendApiUrl) => backendApiUrl !== ""),
        switchMap((backendApiUrl) => {
          return this.http.post(`${backendApiUrl}/process/${data.processId}/result-list-get`, {
            object_ids: data.objectIds
          })
        })
      )
  }


  createProcessResult(processId: string): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        return this.http.post(`${backendApiUrl}/process/${processId}/result-list`, {})
      })
    )
  }

  updateProcessResult(data: {processId: string, resultId: string, data: ProcessResultI}): Observable<any>{
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        return this.http.patch(`${backendApiUrl}/process/${data.processId}/result-list/${data.resultId}`, data.data)
      })
    )
  }

  updateProcessResultTask(resultId: string, taskId:string, data: any): Observable<any>{
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        return this.http.post(`${backendApiUrl}/result-list/${resultId}/${taskId}`, data)
      })
    )
  }

  patchProcessResultTask(data:{processId: string, resultId: string, taskId:string, activityId: string, body: any}): Observable<any>{
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        return this.http.patch(`${backendApiUrl}/process/${data.processId}/result-list/${data.resultId}/${data.taskId}/${data.activityId}`, data.body)
      })
    )
  }

  getProcessResult(data:{processId: string, resultId: string}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        return this.http.get(`${backendApiUrl}/process/${data.processId}/result-list/${data.resultId}`)
      })
    )
  }

  getProcessResultGridData(data:{
    processId: string,
  }): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      withLatestFrom(this.store.select(selectSearchParameters)),
      switchMap(([backendApiUrl, searchParameters]) => {
        let body: any = {}

        if (searchParameters.page)  body.page = `${searchParameters.page}`;
        if (searchParameters.entries) body.limit = `${searchParameters.entries}`;
        if (searchParameters.bookmarked_filter === 'active') body.bookmarked_filter = searchParameters.bookmarked_filter;
        if (searchParameters.process_status_filter) body.process_status = searchParameters.process_status_filter;
        if (searchParameters.process_tasks_filter) body.process_tasks = searchParameters.process_tasks_filter;
        if (searchParameters.search) body.search = searchParameters.search;
        if (searchParameters.orderBy) body.order_by = searchParameters.orderBy;
        if (searchParameters.order) body.order = searchParameters.order;
        if (searchParameters.dynamic_filter) {
          const dynamicFilterStr = Object.keys(searchParameters.dynamic_filter).reduce((acc: string, curr: string) => {
            if (searchParameters.dynamic_filter[curr]) return acc + `$${curr}=${searchParameters.dynamic_filter[curr]}`;
            return acc;
          }, "").slice(1)
          if (dynamicFilterStr) body.filter = dynamicFilterStr;
        }

        return this.http.post(`${backendApiUrl}/process/${data.processId}/stats`, body)
      })
    )
  }



  removeProcessResult(data: {processId: string, resultId: string}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      withLatestFrom(this.store.select(selectSearchParameters)),
      switchMap(([backendApiUrl, searchParameters]) => {
        let body: any = {}

        if (searchParameters.page)  body.page = `${searchParameters.page}`;
        if (searchParameters.entries) body.limit = `${searchParameters.entries}`;
        if (searchParameters.bookmarked_filter === 'active') body.bookmarked_filter = searchParameters.bookmarked_filter;
        if (searchParameters.process_status_filter) body.process_status = searchParameters.process_status_filter;
        if (searchParameters.process_tasks_filter) body.process_tasks = searchParameters.process_tasks_filter;
        if (searchParameters.search) body.search = searchParameters.search;
        if (searchParameters.orderBy) body.order_by = searchParameters.orderBy;
        if (searchParameters.order) body.order = searchParameters.order;
        if (searchParameters.dynamic_filter) {
          const dynamicFilterStr = Object.keys(searchParameters.dynamic_filter).reduce((acc: string, curr: string) => {
            if (searchParameters.dynamic_filter[curr]) return acc + `$${curr}=${searchParameters.dynamic_filter[curr]}`;
            return acc;
          }, "").slice(1)
          if (dynamicFilterStr) body.filter = dynamicFilterStr;
        }


        return this.http.delete(
          `${backendApiUrl}/process/${data.processId}/result-list/${data.resultId}`,{body: body})
      })
    )
  }

  executeProcessResultAction(data:{processId: string, resultId: string, taskId: string, status: TaskStateI}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        const url = `${backendApiUrl}/process/${data.processId}/result-list/${data.resultId}/${data.taskId}/status?`
        const body = {
          status: data.status
        }
        return this.http.patch(
          url,body)
      })
    )
  }

  updateProcessResultPermissions(data:{processId: string, resultId: string, body: TaskPermissionsObjectI}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        const url = `${backendApiUrl}/process/${data.processId}/result-list/${data.resultId}/permissions`
        return this.http.patch(url,data.body)
      })
    )
  }

  removeProcessPermissions(data:{resultId: string, processId: string, body: TaskPermissionsObjectI}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        const url = `${backendApiUrl}/process/${data.processId}/result-list/${data.resultId}/permissions/${data.body.object.username}`
        return this.http.delete(url)
      })
    )
  }

  getTypeaheadOptions(data:{processId: string, query: string}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        const url = `${backendApiUrl}/process/${data.processId}/result-list/typeahead?search=${data.query}`
        return this.http.get(url)
      })
    )
  }


  createTaskTree(processTasks: TaskI[]) {

    //Convert tasks in a tree using id and parentId
    const tasks = JSON.parse(JSON.stringify(processTasks));

    //Maping of task id and position in array of tasks
    const idMapping = tasks.reduce((accum:any, curr:TaskI, i:number) => {
      accum[curr.id] = i;
      return accum;
    },{})

    //Build task tree using references
    let taskTree: TaskI[] = [];
    tasks.forEach((task: TaskI) => {
      if (!task.parentId) {
        taskTree = [...taskTree,task];
        return;
      }
      let parentTask = tasks[idMapping[task.parentId]]
      parentTask.children = [...(parentTask.children || []), task]
    });

    return taskTree
  }

  dispatchActionButton(data:{buttonId: string, processId: string, resultId: string}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        const url = `${backendApiUrl}/process/${data.processId}/action-buttons/${data.resultId}/${data.buttonId}`
        return this.http.post(url, {})
      })
    )
  }

  bulkRemoveProcessResult(data:{processId: string; objectIds: string[]}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl) => {
        const body = {
          'object_ids': data.objectIds
        };

        return this.http.delete(
          `${backendApiUrl}/process/${data.processId}/result-list`,
          {body: body}
        )
      })
    )
  }

  bulkStatusProcessResult(data:{processId: string; objectIds: string[]; status: TaskStateI}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl) => {
        const body = {
          'object_ids': data.objectIds,
          'process_status': data.status
        };

        return this.http.patch(
          `${backendApiUrl}/process/${data.processId}/result-list`,
          body
        )
      })
    )
  }

  bulkStatusTaskResult(data:{processId: string; taskId: string; objectIds: string[]; status: TaskStateI}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl) => {
        const body = {
          'object_ids': data.objectIds,
          'status': data.status
        };

        return this.http.patch(
          `${backendApiUrl}/process/${data.processId}/tasks/${data.taskId}`,
          body
        )
      })
    )
  }

  getProcessJob(data: {jobId: string}): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl) => {
        return this.http.get(
          `${backendApiUrl}/bulk-jobs/${data.jobId}`
        )
      })
    )
  }

  executeBulkActionButton(data:{
    processId: string,
    buttonId: string,
    resultIds: string[],
    all: boolean
  }): Observable<any> {
    return this.store.select(selectBackendApi).pipe(
      filter((val) => val !== ""),
      take(1),
      switchMap((backendApiUrl: string) => {
        const url = `${backendApiUrl}/process/${data.processId}/buttons/${data.buttonId}`
        const body = {
          all: data.all,
          result_ids: data.resultIds,
        }
        return this.http.post(
          url,
          body
        )
      })
    )
  }


}
